import { digger } from '@cbre/digger';
import { ReportRecord } from '../../../../models/report.model';
import { LovValues, DisplayFunctionOptions, VisibilitySettings } from '../../../../models/template.model';
import { map } from 'rxjs/operators';
import { currencyLov, extraCurrencyValues } from '../us/us.common';
import { formatNumberByMIQLocale } from './common';

const askingRateFields = {
  yearly: {
    valueFields: {
      min: 'usages/minimum_yearly_direct_lease_rate_amount',
      max: 'usages/maximum_yearly_direct_lease_rate',
    },
    UomFields: {
      minUom: 'usages/minimum_yearly_direct_lease_rate_amount_uom_desc',
      maxUom: 'usages/maximum_yearly_direct_lease_rate_uom_desc',
    },
  },
  monthly: {
    valueFields: {
      min: 'usages/minimum_monthly_direct_lease_rate',
      max: 'usages/maximum_monthly_direct_lease_rate',
    },
    UomFields: {
      minUom: 'usages/minimum_monthly_direct_lease_rate_uom_desc',
      maxUom: 'usages/maximum_monthly_direct_lease_rate_uom_desc',
    },
  },
};

function getDirectAskingRateYearly(record: ReportRecord, options: DisplayFunctionOptions) {
  return currencyLov(options).pipe(
    map((currencyLOV: LovValues[]) => {
      /*
       * prepare and add extra currencies as we currently do not have
       * swedish or norwegian currency symbols.
       */
      currencyLOV.push(...extraCurrencyValues);

      const yearlyFields = askingRateFields.yearly;
      const directAskingRentVal = Object.values(yearlyFields.valueFields).map(rentField =>
        digger(record.data, rentField)
      );
      const directUOMVal = Object.values(yearlyFields.UomFields).map(rentUOM => digger(record.data, rentUOM));

      const filteredRent = directAskingRentVal.filter(minMaxAskingRate => {
        if (Array.isArray(minMaxAskingRate)) {
          return minMaxAskingRate[0] != null;
        }
        return minMaxAskingRate != null;
      });
      const filteredUOMVal = directUOMVal.filter(minMaxAskingRate => {
        if (Array.isArray(minMaxAskingRate)) {
          return minMaxAskingRate[0] != null;
        }
        return minMaxAskingRate != null;
      });
      const flattedUOMVal = filteredUOMVal.reduce((acc, value) => acc.concat(value), []);
      const symbol = currencyLOV.find(currency => currency.key === flattedUOMVal[0])?.value;
      if (filteredRent.length === 2 && filteredUOMVal.length === 2) {
        return symbol != null ? `${symbol[0]}${filteredRent[0]}` : `${filteredRent[0]} ${filteredUOMVal[0]}`;
      }
      return symbol != null ? `${symbol}${filteredRent}` : `${filteredRent} ${filteredUOMVal}`;
    })
  );
}

function getDirectAskingRateMonthly(record: ReportRecord, options: DisplayFunctionOptions) {
  return currencyLov(options).pipe(
    map((currencyLOV: LovValues[]) => {
      /*
       * prepare and add extra currencies as we currently do not have
       * swedish or norwegian currency symbols.
       */
      currencyLOV.push(...extraCurrencyValues);
      const monthlyFields = askingRateFields.monthly;
      const directAskingRentVal = Object.values(monthlyFields.valueFields).map(rentField =>
        digger(record.data, rentField)
      );
      const directUOMVal = Object.values(monthlyFields.UomFields).map(rentUOM => digger(record.data, rentUOM));

      const filteredRent = directAskingRentVal.filter(minMaxAskingRate => {
        if (Array.isArray(minMaxAskingRate)) {
          return minMaxAskingRate[0] != null;
        }
        return minMaxAskingRate != null;
      });
      const filteredUOMVal = directUOMVal.filter(uomVal =>
        Array.isArray(uomVal) ? uomVal[0] != null : uomVal != null
      );
      const flattedUOMVal = filteredUOMVal.reduce((acc, value) => acc.concat(value), []);
      const symbol = currencyLOV.find(currency => currency.key === flattedUOMVal[0])?.value;
      const flattedFilteredVals = [].concat(...filteredRent);
      if (filteredRent.length === 2 && filteredUOMVal.length === 2) {
        return symbol != null
          ? `${symbol[0]}${formatNumberByMIQLocale(flattedFilteredVals[0], options.locale)}`
          : `${formatNumberByMIQLocale(flattedFilteredVals[0], options.locale)} ${filteredUOMVal[0]}`;
      }
      return symbol != null ? `${symbol}${filteredRent}` : `${filteredRent} ${filteredUOMVal}`;
    })
  );
}

function getDirectAskingRateMonthlyVisibility(record: ReportRecord, settings: VisibilitySettings): boolean {
  const { field } = settings;
  if (!field) {
    return false;
  }

  const monthlyFields = askingRateFields.monthly;
  const directAskingRentVal = Object.values(monthlyFields.valueFields).map(rentField => digger(record.data, rentField));
  const directUOMVal = Object.values(monthlyFields.UomFields).map(rentUOM => digger(record.data, rentUOM));

  const filteredRent = directAskingRentVal.filter(minMaxAskingRate => {
    if (Array.isArray(minMaxAskingRate)) {
      return minMaxAskingRate[0] != null;
    }
    return minMaxAskingRate != null;
  });
  if (filteredRent.length > 0) {
    const filteredVals = filteredRent.filter(val => (Array.isArray(val) ? val[0] != null : val != null));
    const flattedFilteredVals = [].concat(...filteredVals);
    /* Return true when filteredVals has a single value */
    if (flattedFilteredVals.length === 1) {
      return true;
    }
    if (flattedFilteredVals.length === 2) {
      return flattedFilteredVals[0] === flattedFilteredVals[1];
    }
  }
  return false; //both values are null;
}

function getDirectAskingRateYearlyVisibility(record: ReportRecord, settings: VisibilitySettings): boolean {
  const { field } = settings;
  if (!field) {
    return false;
  }
  const yearlyFields = askingRateFields.yearly;
  const directAskingRentVal = Object.values(yearlyFields.valueFields).map(rentField => digger(record.data, rentField));
  const filteredRents = directAskingRentVal.filter(minMaxAskingRate =>
    Array.isArray(minMaxAskingRate) ? minMaxAskingRate[0] != null : minMaxAskingRate != null
  );
  if (filteredRents.length > 0) {
    const filteredVals = filteredRents.filter(val => (Array.isArray(val) ? val[0] != null : val != null));
    const flattedFilteredVals = [].concat(...filteredVals);
    /* Return true when filteredVals has a single value */
    if (flattedFilteredVals.length === 1) {
      return true;
    }
    if (flattedFilteredVals.length === 2) {
      return flattedFilteredVals[0] === flattedFilteredVals[1];
    }
  }
  return false;
}

export const emeaDirectAskingRateFunctions = {
  893: {
    // 893 - Direct Asking Rate Yearly (Norway, Netherlands, Sweden)
    displayFn: getDirectAskingRateYearly,
    isVisible: getDirectAskingRateYearlyVisibility,
  },
  894: {
    // 894 - Direct Asking Rate Monthly (Italy, Poland, Finland)
    displayFn: getDirectAskingRateMonthly,
    isVisible: getDirectAskingRateMonthlyVisibility,
  },
};
